import { useCallback } from "react";

import { post } from "../api-client/post";
import { useIsLoggedOn } from "../stores/hooks/useIsLoggedOn";
import { useNullableDealerAccountInfo } from "../stores/hooks/useNullableDealerAccountInfo";

/**
 * Feature keys accepted by the API allowlist
 * (`LogFeatureUsageController::FEATURES` in tirelocator).
 */
export type TrackedFeature =
  | "bookingSystem"
  | "chassisSearch"
  | "createPackage"
  | "dealerOrderPlaced"
  | "emailComparison"
  | "hunterTire"
  | "myLeads"
  | "myOrders"
  | "myQuotes"
  | "orderNow"
  | "packageEditor"
  | "partNumberSearch"
  | "paymentCalculator"
  | "pricingAnalysis"
  | "pricingAnalysisCms";

export type LogFeatureUsage = (feature: TrackedFeature) => void;

/**
 * TW-926: records that the signed-in dealer user selected a tracked feature,
 * so the business can see whether the feature is actually being used.
 *
 * Fire-and-forget: the API only stores usage for authenticated tokens, so the
 * call is skipped entirely for guests, and a failed tracking call must never
 * interrupt the shopper (the fetcher already reports failures to Bugsnag).
 */
export const useLogFeatureUsage = (): LogFeatureUsage => {
  const dealer = useNullableDealerAccountInfo();
  const isLoggedOn = useIsLoggedOn();
  const dealerId = dealer?.dealerId;

  return useCallback(
    (feature: TrackedFeature): void => {
      if (!isLoggedOn || !dealerId) {
        return;
      }

      post("/retailSiteWebService/tracking/logFeatureUsage.php", {
        dealerId,
        feature,
      }).catch(() => undefined);
    },
    [dealerId, isLoggedOn]
  );
};
